/**
 * CNY text field ↔ USD-cent spend cap.
 *
 * Shared by API-key limits and the org-member monthly limit. The reader
 * types RMB; the wire value is integer USD cents at the display rate.
 * Callers map {@link SpendLimitParseCode} onto their own copy — revoking
 * a key and disabling a member are different remedies for the same `0`.
 */
import { usdCentsToCnyCents } from './formatters.ts';

export function isValidDisplayRate(cnyPerUsd: number | null | undefined): cnyPerUsd is number {
  return typeof cnyPerUsd === 'number' && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0;
}

/** Prefill for a CNY amount input. Whole yuan drop the trailing `.00`. */
export function formatLimitInputValue(
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
): string {
  if (typeof usdCents !== 'number' || !Number.isFinite(usdCents)) return '';
  const cnyCents = usdCentsToCnyCents(usdCents, cnyPerUsd);
  if (cnyCents === null) return '';
  const yuan = (cnyCents / 100).toFixed(2);
  return yuan.endsWith('.00') ? yuan.slice(0, -3) : yuan;
}

export type SpendLimitParseCode = 'invalid' | 'zero' | 'rate_unavailable';

export type SpendLimitParseResult =
  | { readonly ok: true; readonly usdCents: number | null }
  | { readonly ok: false; readonly code: SpendLimitParseCode };

/**
 * Empty means no ceiling (`null`). Anything else must be a non-negative
 * RMB amount with at most two decimals, then converted at `cnyPerUsd`.
 *
 * `0` is refused on the CONVERTED cents (#1620): a small enough RMB
 * amount rounds down to 0 USD cents, and the server rejects a stored 0
 * because the rating counters read it as "no ceiling" while the gate it
 * replaced read it as a hard stop.
 */
export function parseSpendLimitInput(
  raw: string,
  cnyPerUsd: number | null | undefined,
): SpendLimitParseResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: true, usdCents: null };
  }
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) {
    return { ok: false, code: 'invalid' };
  }
  if (!isValidDisplayRate(cnyPerUsd)) {
    return { ok: false, code: 'rate_unavailable' };
  }
  const parts = trimmed.split('.');
  const wholeRaw = parts[0] ?? '0';
  const fractionRaw = parts[1] ?? '';
  const cnyCents =
    Number.parseInt(wholeRaw, 10) * 100 + Number.parseInt(fractionRaw.padEnd(2, '0'), 10);
  if (!Number.isFinite(cnyCents) || cnyCents < 0) {
    return { ok: false, code: 'invalid' };
  }
  const usdCents = Math.round(cnyCents / cnyPerUsd);
  if (usdCents === 0) {
    return { ok: false, code: 'zero' };
  }
  return { ok: true, usdCents };
}
